import React, { useState, useEffect } from 'react';
import { getProposals, getSignalsAsProposals, getSmartProposals, getPortfolio, getAlpacaStatus, getStockIndicators, approveProposal, rejectProposal } from '../api';

const API = process.env.REACT_APP_API_URL || '';


const DEMO_PROPOSALS = [
  {
    proposal_id: 'demo-paper-aapl',
    order_type: 'long_entry',
    side: 'BUY',
    symbol: 'AAPL',
    qty: 5,
    estimated_price: 198.45,
    signal: 'BUY',
    composite: 0.42,
    demo: true,
  },
];

const ACK_TEXT = 'I reviewed this order and understand that I am making my own trading decision. Thankful Money provides informational signal context only and does not provide investment advice.';

export default function ProposalsPage() {
  const [proposals, setProposals] = useState([]);
  const [holdings, setHoldings] = useState({});
  const [account, setAccount] = useState(null);
  const [indicators, setIndicators] = useState({});
  const [expanded, setExpanded] = useState({});
  const [signalDate, setSignalDate] = useState('');
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [filters, setFilters] = useState({});
  const [budget, setBudget] = useState(null);
  const [summary, setSummary] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [proposalsSubmittedToday, setProposalsSubmittedToday] = useState(false);
  const [showFlagged, setShowFlagged] = useState(true);

  useEffect(() => {
    // Try smart proposals first (preference-aware), fall back to raw signals
    Promise.all([
      getSmartProposals().catch(() => null),
      getPortfolio().catch(() => ({ positions: [] })),
      getAlpacaStatus().catch(() => null),
    ]).then(async ([smartData, portData, acctData]) => {
      const h = {};
      let totalValue = 0;
      (portData.positions || []).forEach(p => {
        h[p.symbol] = p.qty;
        totalValue += p.market_value || 0;
      });
      setHoldings(h);
      if (acctData) setAccount({ ...acctData, positions_value: totalValue, positions_count: (portData.positions || []).length });

      let raw;
      if (smartData && smartData.proposals) {
        raw = smartData.proposals;
        setSignalDate(smartData.date || '');
        setBudget(smartData.budget || null);
        setSummary(smartData.summary || null);
        if (smartData.proposals_submitted_today) setProposalsSubmittedToday(true);
      } else {
        // Fallback to raw signals
        try {
          const sigData = await getSignalsAsProposals();
          raw = sigData.proposals || DEMO_PROPOSALS;
          setSignalDate(sigData.date || '');
        } catch {
          try {
            const d = await getProposals();
            raw = d.proposals || DEMO_PROPOSALS;
          } catch {
            raw = DEMO_PROPOSALS;
          }
        }
      }
      const filtered = raw.filter(p => p.side === 'BUY' || (h[p.symbol] && h[p.symbol] > 0));
      setProposals(filtered);
      const syms = [...new Set(filtered.map(p => p.symbol))];
      if (syms.length) getStockIndicators(syms).then(setIndicators).catch(() => {});
      setLoading(false);
    });
  }, []);

  const edit = (id) => edits[id] || {};
  const setEdit = (id, patch) => setEdits(p => ({ ...p, [id]: { ...p[id], ...patch } }));

  // ── Bulk actions ──
  const decideIds = (ids, decision) => {
    const u = { ...edits };
    ids.forEach(id => { u[id] = { ...u[id], decision }; });
    setEdits(u);
  };
  const approveIds = (ids) => decideIds(ids, 'approve');
  const rejectIds = (ids) => decideIds(ids, 'reject');
  const clearIds = (ids) => {
    const u = { ...edits };
    ids.forEach(id => { delete u[id]?.decision; if (u[id] && !Object.keys(u[id]).length) delete u[id]; });
    setEdits({ ...u });
  };

  const toggleFilter = (key) => setFilters(f => ({ ...f, [key]: !f[key] }));
  const strongOnly = () => setFilters({ STRONG_BUY: true, BUY: false, SELL: false, STRONG_SELL: true });
  const showAll = () => setFilters({ STRONG_BUY: true, BUY: true, SELL: true, STRONG_SELL: true });
  const filteredProposals = proposals.filter(p => {
    const sig = p.signal || '';
    if (sig === 'STRONG BUY' && filters.STRONG_BUY === false) return false;
    if (sig === 'BUY' && filters.BUY === false) return false;
    if (sig === 'OVERSOLD' && filters.OVERSOLD === false) return false;
    if (sig === 'SELL' && filters.SELL === false) return false;
    if (sig === 'OVERBOUGHT' && filters.OVERBOUGHT === false) return false;
    if (sig === 'STRONG SELL' && filters.STRONG_SELL === false) return false;
    if (!showFlagged && p.flag && p.flag !== 'fit') return false;
    return true;
  });

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const allIds = filteredProposals.map(p => p.proposal_id);
  const fitIds = filteredProposals.filter(p => !p.flag || p.flag === 'fit').map(p => p.proposal_id);

  const toggleCollapse = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  // ── Counts & cost summary ──
  const decidedCount = Object.values(edits).filter(e => e.decision).length;
  const approvedCount = Object.values(edits).filter(e => e.decision === 'approve').length;
  const rejectedCount = Object.values(edits).filter(e => e.decision === 'reject').length;
  const modifiedCount = Object.values(edits).filter(e => e.qty !== undefined).length;

  const byId = Object.fromEntries(proposals.map(p => [p.proposal_id, p]));
  let totalBuyCost = 0, totalSellValue = 0, buyCount = 0, sellCount = 0;
  for (const [id, e] of Object.entries(edits)) {
    if (e.decision !== 'approve') continue;
    const p = byId[id];
    if (!p) continue;
    const qty = e.qty !== undefined ? e.qty : p.qty;
    const cost = qty * (p.estimated_price || 0);
    if (p.side === 'BUY') { totalBuyCost += cost; buyCount++; }
    else { totalSellValue += cost; sellCount++; }
  }
  const cash = Number(account?.cash || 0);
  const remainingCash = cash - totalBuyCost + totalSellValue;

  const canSubmit = acknowledged && !submitting && remainingCash >= 0;

  // ── Submit ──
  const handleSubmit = async () => {
    if (approvedCount > 0 && !acknowledged) return;
    setSubmitting(true);
    const byId = Object.fromEntries(proposals.map(p => [p.proposal_id, p]));
    for (const [id, e] of Object.entries(edits)) {
      if (!e.decision) continue;
      if (byId[id]?.demo) continue;
      try {
        if (e.qty !== undefined) {
          await fetch(`${API}/api/proposals/${encodeURIComponent(id)}/modify`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ qty: e.qty }),
          });
        }
        if (e.decision === 'approve') {
          const p = byId[id];
          await approveProposal(id, {
            symbol: p.symbol, side: p.side, qty: e.qty || p.qty,
            order_type: p.order_type, signal: p.signal, composite: p.composite,
          });
        } else {
          await rejectProposal(id);
        }
      } catch (err) { console.error(id, err); }
    }
    // Build summary of what was just submitted
    const summaryItems = [];
    for (const [id, e] of Object.entries(edits)) {
      if (e.decision !== 'approve') continue;
      const p = byId[id];
      if (!p || p.demo) continue;
      const qty = e.qty !== undefined ? e.qty : p.qty;
      summaryItems.push({
        symbol: p.symbol,
        side: p.side,
        qty,
        price: p.estimated_price || 0,
        cost: qty * (p.estimated_price || 0),
        signal: p.signal,
      });
    }
    setSubmitted(summaryItems.length > 0 ? summaryItems : null);
    setEdits({});
    setAcknowledged(false);
    setSubmitting(false);
  };

  if (loading) return (
    <div>
      <div style={s.header}>
        <div>
          <div style={{ ...s.skel, width: 180, height: 22, marginBottom: 8 }} />
          <div style={{ ...s.skel, width: 320, height: 13 }} />
        </div>
      </div>
      <div style={{ ...s.portfolioBar, gap: 16 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ ...s.skel, width: 70, height: 10, marginBottom: 6 }} />
            <div style={{ ...s.skel, width: 100, height: 16 }} />
          </div>
        ))}
      </div>
      <div style={{ ...s.group, overflow: 'hidden' }}>
        <div style={{ ...s.groupHead }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ ...s.skel, width: 28, height: 28, borderRadius: 6 }} />
            <div style={{ ...s.skel, width: 160, height: 14 }} />
          </div>
        </div>
        {[1,2,3,4,5,6,7,8].map(i => (
          <div key={i} style={{ ...s.row, padding: '10px 18px' }}>
            <div style={{ ...s.skel, width: 20, height: 14 }} />
            <div style={{ ...s.skel, width: 32, height: 12 }} />
            <div style={{ ...s.skel, width: 48, height: 14 }} />
            <div style={{ ...s.skel, width: 24, height: 12 }} />
            <div style={{ ...s.skel, width: 40, height: 20, borderRadius: 4 }} />
            <div style={{ ...s.skel, width: 56, height: 12 }} />
            <div style={{ ...s.skel, width: 56, height: 12 }} />
            <div style={{ ...s.skel, width: 64, height: 18, borderRadius: 4 }} />
            <div style={{ ...s.skel, width: 36, height: 14 }} />
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 3 }}>
              <div style={{ ...s.skel, width: 26, height: 26, borderRadius: 5 }} />
              <div style={{ ...s.skel, width: 26, height: 26, borderRadius: 5 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  if (!proposals.length) return (
    <div>
      <h1 style={s.h1}>Order Review</h1>
      <div style={s.empty}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
        <div style={s.emptyTitle}>No Pending Order Drafts</div>
        <div style={s.emptyText}>New review items may appear after the nightly signal run (11 PM UTC).</div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Order Review</h1>
          <p style={s.sub}>{filteredProposals.length} of {proposals.length} signal-based order drafts · {decidedCount} reviewed{signalDate && ` · Signals: ${signalDate}`}</p>
        </div>
      </div>

      {proposals.some(p => p.demo) && (
        <div style={s.demoNotice}>
          Paper-trading demo order draft shown for OAuth review. Live-trading workflow remains gated until paper-trading onboarding is complete.
        </div>
      )}



      {submitted && (
        <div style={s.submittedBox}>
          <div style={s.submittedHeader}>
            <span style={{ fontSize: 18 }}>✅</span>
            <span style={s.submittedTitle}>Orders Submitted</span>
            <button onClick={() => { setSubmitted(null); window.location.reload(); }} style={s.submittedRefresh}>Load New Proposals</button>
          </div>
          <div style={s.submittedTable}>
            <div style={s.submittedRow}>
              <span style={{ ...s.submittedCell, fontWeight: 700, color: 'var(--t3)', fontSize: 9 }}>SIDE</span>
              <span style={{ ...s.submittedCell, fontWeight: 700, color: 'var(--t3)', fontSize: 9, flex: 1 }}>SYMBOL</span>
              <span style={{ ...s.submittedCell, fontWeight: 700, color: 'var(--t3)', fontSize: 9 }}>QTY</span>
              <span style={{ ...s.submittedCell, fontWeight: 700, color: 'var(--t3)', fontSize: 9 }}>PRICE</span>
              <span style={{ ...s.submittedCell, fontWeight: 700, color: 'var(--t3)', fontSize: 9 }}>COST</span>
              <span style={{ ...s.submittedCell, fontWeight: 700, color: 'var(--t3)', fontSize: 9 }}>SIGNAL</span>
            </div>
            {submitted.map((o, i) => (
              <div key={i} style={s.submittedRow}>
                <span style={{ ...s.submittedCell, color: o.side === 'BUY' ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>{o.side}</span>
                <span style={{ ...s.submittedCell, fontWeight: 600, flex: 1 }}>{o.symbol}</span>
                <span style={s.submittedCell}>{o.qty}</span>
                <span style={s.submittedCell}>${o.price.toFixed(2)}</span>
                <span style={{ ...s.submittedCell, fontWeight: 600 }}>${o.cost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                <span style={s.submittedCell}>{o.signal}</span>
              </div>
            ))}
            <div style={{ ...s.submittedRow, borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 6 }}>
              <span style={s.submittedCell}></span>
              <span style={{ ...s.submittedCell, flex: 1, fontWeight: 700 }}>Total</span>
              <span style={s.submittedCell}>{submitted.reduce((s, o) => s + o.qty, 0)}</span>
              <span style={s.submittedCell}></span>
              <span style={{ ...s.submittedCell, fontWeight: 700 }}>${submitted.reduce((s, o) => s + o.cost, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span style={s.submittedCell}></span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 8 }}>
            ⏰ Orders submitted for Alpaca processing at next market open (9:30 AM ET).
          </div>
        </div>
      )}

      {account && (
        <div style={s.portfolioBar}>
          <div style={s.pStat}>
            <span style={s.pLabel}>Cash</span>
            <span style={s.pValue}>${Number(account.cash || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={s.pDivider} />
          <div style={s.pStat}>
            <span style={s.pLabel}>Buying Power</span>
            <span style={s.pValue}>${Number(account.buying_power || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={s.pDivider} />
          <div style={s.pStat}>
            <span style={s.pLabel}>Portfolio</span>
            <span style={s.pValue}>${Number(account.portfolio_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={s.pDivider} />
          <div style={s.pStat}>
            <span style={s.pLabel}>Positions</span>
            <span style={s.pValue}>{account.positions_count || 0} stocks · ${Number(account.positions_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      )}

      {/* ── Budget summary (from smart proposals) ── */}
      {budget && (
        <div style={s.budgetBar}>
          <div style={s.pStat}>
            <span style={s.pLabel}>Spendable Cash</span>
            <span style={s.pValue}>${budget.spendable_cash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={s.pDivider} />
          <div style={s.pStat}>
            <span style={s.pLabel}>Stock Budget Left</span>
            <span style={{ ...s.pValue, color: budget.remaining_stock_budget <= 0 ? 'var(--red)' : 'var(--t1)' }}>${budget.remaining_stock_budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={s.pDivider} />
          <div style={s.pStat}>
            <span style={s.pLabel}>ETF Budget Left</span>
            <span style={{ ...s.pValue, color: budget.remaining_etf_budget <= 0 ? 'var(--red)' : 'var(--t1)' }}>${budget.remaining_etf_budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={s.pDivider} />
          <div style={s.pStat}>
            <span style={s.pLabel}>Total Buy Cost</span>
            <span style={s.pValue}>${budget.total_buy_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div style={s.pDivider} />
          <div style={s.pStat}>
            <span style={s.pLabel}>Cash After</span>
            <span style={{ ...s.pValue, color: budget.cash_after < 0 ? 'var(--red)' : 'var(--green)' }}>${budget.cash_after.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      )}

      {/* ── Today's proposals already submitted ── */}
      {proposalsSubmittedToday && !submitted && (
        <div style={s.todaysBanner}>
          <span style={{ fontSize: 14 }}>✅</span>
          <span style={s.todaysBannerText}>
            Today's recommendations already submitted{budget?.already_spent_today > 0 && <span style={{ color: 'var(--t1)', fontWeight: 600 }}> · ${budget.already_spent_today.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} deployed</span>}
          </span>
        </div>
      )}

      {/* ── Summary counts ── */}
      {summary && (
        <div style={s.summaryBar}>
          <span style={{ color: 'var(--green)', fontWeight: 600 }}>{filteredProposals.filter(p => !p.flag || p.flag === 'fit').length} fit</span>
          <span style={{ color: 'var(--t3)', margin: '0 6px' }}>·</span>
          <span style={{ color: 'var(--amber)', fontWeight: 600 }}>{filteredProposals.filter(p => p.flag && p.flag !== 'fit').length} flagged</span>
          <span style={{ color: 'var(--t3)', margin: '0 6px' }}>·</span>
          <span style={{ color: 'var(--t3)' }}>{filteredProposals.length} total signals</span>
          <span style={{ flex: 1 }} />
          <label style={{ fontSize: 11, color: 'var(--t3)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={showFlagged} onChange={e => setShowFlagged(e.target.checked)} style={{ accentColor: 'var(--green)' }} />
            Show flagged
          </label>
        </div>
      )}

      {/* ── Filter bar ── */}
      <div style={s.filterBar}>
        <span style={s.filterLabel}>Filter:</span>
        {[['STRONG_BUY', 'Strong Buy', 'var(--green)'], ['BUY', 'Buy', 'rgba(16,185,129,0.7)'], ['OVERSOLD', 'Oversold', 'rgb(59,130,246)'], ['SELL', 'Sell', 'rgba(239,68,68,0.7)'], ['OVERBOUGHT', 'Overbought', 'rgb(245,158,11)'], ['STRONG_SELL', 'Strong Sell', 'var(--red)']].map(([key, label, color]) => (
          <label key={key} style={{ ...s.filterChip, borderColor: filters[key] !== false ? color : 'var(--border)', color: filters[key] !== false ? color : 'var(--t3)' }}>
            <input type="checkbox" checked={filters[key] !== false} onChange={() => setFilters(f => ({ ...f, [key]: !(f[key] !== false) }))} style={{ display: 'none' }} />
            {filters[key] !== false ? '✓ ' : ''}{label}
          </label>
        ))}
      </div>

      {/* ── Global toolbar ── */}
      <div style={s.globalBar}>
        <button onClick={() => approveIds(fitIds)} style={s.gApprove}>✓ Approve Recommended ({fitIds.length})</button>
        <button onClick={() => rejectIds(allIds)} style={s.gReject}>✗ Reject All</button>
        <button onClick={() => clearIds(allIds)} style={s.gClear}>↺ Reset All</button>
        <span style={s.gSpacer} />
        <span style={s.gCount}>
          {approvedCount > 0 && <span style={{ color: 'var(--green)' }}>{approvedCount} approved</span>}
          {rejectedCount > 0 && <span style={{ color: 'var(--red)', marginLeft: 10 }}>{rejectedCount} rejected</span>}
          {modifiedCount > 0 && <span style={{ color: 'var(--amber)', marginLeft: 10 }}>{modifiedCount} modified</span>}
        </span>
      </div>

      {/* ── Recommended Orders (fit) ── */}
      {(() => {
        const fitItems = filteredProposals.filter(p => !p.flag || p.flag === 'fit');
        const fitIds = fitItems.map(p => p.proposal_id);
        const fitApproved = fitIds.filter(id => edit(id).decision === 'approve').length;
        const fitRejected = fitIds.filter(id => edit(id).decision === 'reject').length;
        if (!fitItems.length) return null;
        return (
          <div style={s.group}>
            <div style={s.groupHead} onClick={() => toggleCollapse('_fit')}>
              <div style={s.groupLeft}>
                <span style={{ ...s.groupIcon, background: 'var(--green-bg)', color: 'var(--green)' }}>✓</span>
                <span style={s.groupLabel}>Recommended Orders</span>
                <span style={s.groupCount}>{fitItems.length}</span>
                {fitApproved > 0 && <span style={{ ...s.miniPill, color: 'var(--green)' }}>✓{fitApproved}</span>}
                {fitRejected > 0 && <span style={{ ...s.miniPill, color: 'var(--red)' }}>✗{fitRejected}</span>}
              </div>
              <div style={s.groupActions} onClick={e => e.stopPropagation()}>
                <button onClick={() => approveIds(fitIds)} style={{ ...s.gBtn, color: 'var(--green)' }}>✓ All</button>
                <button onClick={() => rejectIds(fitIds)} style={{ ...s.gBtn, color: 'var(--red)' }}>✗ All</button>
                <button onClick={() => clearIds(fitIds)} style={{ ...s.gBtn, color: 'var(--t3)' }}>↺</button>
                <span style={s.chevron}>{collapsed['_fit'] ? '▸' : '▾'}</span>
              </div>
            </div>
            {!collapsed['_fit'] && (
              <div>
                <ProposalHeader />
                {fitItems.map(p => <ProposalRow key={p.proposal_id} p={p} e={edit(p.proposal_id)} holdings={holdings} indicators={indicators} expanded={expanded} toggleExpand={toggleExpand} setEdit={setEdit} />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Below Composite (held positions still worth showing) ── */}
      {(() => {
        const bcItems = filteredProposals.filter(p => p.flag === 'below_composite');
        const bcIds = bcItems.map(p => p.proposal_id);
        const bcApproved = bcIds.filter(id => edit(id).decision === 'approve').length;
        const bcRejected = bcIds.filter(id => edit(id).decision === 'reject').length;
        if (!bcItems.length) return null;
        return (
          <div style={s.group}>
            <div style={s.groupHead} onClick={() => toggleCollapse('_below')}>
              <div style={s.groupLeft}>
                <span style={{ ...s.groupIcon, background: 'var(--amber-bg)', color: 'var(--amber)' }}>📉</span>
                <span style={s.groupLabel}>Below Score Threshold</span>
                <span style={s.groupCount}>{bcItems.length}</span>
                {bcApproved > 0 && <span style={{ ...s.miniPill, color: 'var(--green)' }}>✓{bcApproved}</span>}
                {bcRejected > 0 && <span style={{ ...s.miniPill, color: 'var(--red)' }}>✗{bcRejected}</span>}
              </div>
              <div style={s.groupActions} onClick={e => e.stopPropagation()}>
                <button onClick={() => approveIds(bcIds)} style={{ ...s.gBtn, color: 'var(--green)' }}>✓ All</button>
                <button onClick={() => rejectIds(bcIds)} style={{ ...s.gBtn, color: 'var(--red)' }}>✗ All</button>
                <button onClick={() => clearIds(bcIds)} style={{ ...s.gBtn, color: 'var(--t3)' }}>↺</button>
                <span style={s.chevron}>{collapsed['_below'] ? '▸' : '▾'}</span>
              </div>
            </div>
            {!collapsed['_below'] && (
              <div>
                <ProposalHeader />
                {bcItems.map(p => <ProposalRow key={p.proposal_id} p={p} e={edit(p.proposal_id)} holdings={holdings} indicators={indicators} expanded={expanded} toggleExpand={toggleExpand} setEdit={setEdit} flagged />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Over Budget / Position Limit ── */}
      {(() => {
        const obItems = filteredProposals.filter(p => p.flag === 'over_budget' || p.flag === 'over_position');
        const obIds = obItems.map(p => p.proposal_id);
        const obApproved = obIds.filter(id => edit(id).decision === 'approve').length;
        const obRejected = obIds.filter(id => edit(id).decision === 'reject').length;
        if (!obItems.length) return null;
        return (
          <div style={s.group}>
            <div style={s.groupHead} onClick={() => toggleCollapse('_budget')}>
              <div style={s.groupLeft}>
                <span style={{ ...s.groupIcon, background: 'rgba(239,68,68,0.08)', color: 'var(--red)' }}>💰</span>
                <span style={s.groupLabel}>Over Budget / Position Limit</span>
                <span style={s.groupCount}>{obItems.length}</span>
                {obApproved > 0 && <span style={{ ...s.miniPill, color: 'var(--green)' }}>✓{obApproved}</span>}
                {obRejected > 0 && <span style={{ ...s.miniPill, color: 'var(--red)' }}>✗{obRejected}</span>}
              </div>
              <div style={s.groupActions} onClick={e => e.stopPropagation()}>
                <button onClick={() => approveIds(obIds)} style={{ ...s.gBtn, color: 'var(--green)' }}>✓ All</button>
                <button onClick={() => rejectIds(obIds)} style={{ ...s.gBtn, color: 'var(--red)' }}>✗ All</button>
                <button onClick={() => clearIds(obIds)} style={{ ...s.gBtn, color: 'var(--t3)' }}>↺</button>
                <span style={s.chevron}>{collapsed['_budget'] ?? true ? '▸' : '▾'}</span>
              </div>
            </div>
            {!(collapsed['_budget'] ?? true) && (
              <div>
                <ProposalHeader />
                {obItems.map(p => <ProposalRow key={p.proposal_id} p={p} e={edit(p.proposal_id)} holdings={holdings} indicators={indicators} expanded={expanded} toggleExpand={toggleExpand} setEdit={setEdit} flagged />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── No Position to Sell ── */}
      {(() => {
        const npItems = filteredProposals.filter(p => p.flag === 'no_position');
        const npIds = npItems.map(p => p.proposal_id);
        if (!npItems.length) return null;
        return (
          <div style={s.group}>
            <div style={s.groupHead} onClick={() => toggleCollapse('_nopos')}>
              <div style={s.groupLeft}>
                <span style={{ ...s.groupIcon, background: 'var(--bg-3)', color: 'var(--t3)' }}>📭</span>
                <span style={s.groupLabel}>Sell Signals — Not Held</span>
                <span style={s.groupCount}>{npItems.length}</span>
              </div>
              <div style={s.groupActions} onClick={e => e.stopPropagation()}>
                <span style={s.chevron}>{collapsed['_nopos'] ?? true ? '▸' : '▾'}</span>
              </div>
            </div>
            {!(collapsed['_nopos'] ?? true) && (
              <div>
                <ProposalHeader />
                {npItems.map(p => <ProposalRow key={p.proposal_id} p={p} e={edit(p.proposal_id)} holdings={holdings} indicators={indicators} expanded={expanded} toggleExpand={toggleExpand} setEdit={setEdit} flagged />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Excluded / Disabled ── */}
      {(() => {
        const exItems = filteredProposals.filter(p => p.flag === 'excluded' || p.flag === 'disabled_class');
        if (!exItems.length) return null;
        return (
          <div style={s.group}>
            <div style={s.groupHead} onClick={() => toggleCollapse('_excluded')}>
              <div style={s.groupLeft}>
                <span style={{ ...s.groupIcon, background: 'var(--bg-3)', color: 'var(--t3)' }}>🚫</span>
                <span style={s.groupLabel}>Excluded</span>
                <span style={s.groupCount}>{exItems.length}</span>
              </div>
              <div style={s.groupActions} onClick={e => e.stopPropagation()}>
                <span style={s.chevron}>{collapsed['_excluded'] ?? true ? '▸' : '▾'}</span>
              </div>
            </div>
            {!(collapsed['_excluded'] ?? true) && (
              <div>
                <ProposalHeader />
                {exItems.map(p => <ProposalRow key={p.proposal_id} p={p} e={edit(p.proposal_id)} holdings={holdings} indicators={indicators} expanded={expanded} toggleExpand={toggleExpand} setEdit={setEdit} flagged />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Sticky submit bar ── */}
      {decidedCount > 0 && (
        <div style={s.submitBar}>
          <div style={s.submitLeft}>
            {approvedCount > 0 && <span style={{ color: 'var(--green)', fontWeight: 600 }}>{approvedCount} approved</span>}
            {rejectedCount > 0 && <span style={{ color: 'var(--red)', fontWeight: 600, marginLeft: 12 }}>{rejectedCount} rejected</span>}
            {modifiedCount > 0 && <span style={{ color: 'var(--amber)', fontWeight: 600, marginLeft: 12 }}>{modifiedCount} qty changed</span>}
            {approvedCount > 0 && (
              <div style={s.costSummary}>
                {buyCount > 0 && <span>Buy {buyCount} stocks: <span style={{ color: 'var(--red)', fontWeight: 700 }}>-${totalBuyCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>}
                {sellCount > 0 && <span style={{ marginLeft: 12 }}>Sell {sellCount} stocks: <span style={{ color: 'var(--green)', fontWeight: 700 }}>+${totalSellValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>}
                <span style={{ marginLeft: 12 }}>Cash after: <span style={{ color: remainingCash >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>${remainingCash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
              </div>
            )}
            {remainingCash < 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>
                ⚠ Approved orders exceed available cash by ${Math.abs(remainingCash).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Remove or reject some orders to proceed.
              </div>
            )}
            {approvedCount > 0 && (
              <label style={s.ackLabel}>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={e => setAcknowledged(e.target.checked)}
                  style={s.ackCheckbox}
                />
                <span>{ACK_TEXT}</span>
              </label>
            )}
          </div>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...s.submitBtn,
              opacity: canSubmit ? 1 : 0.45,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Submitting...' : remainingCash < 0 ? 'Insufficient Cash' : `Submit ${decidedCount} User Decisions`}
          </button>
        </div>
      )}

      <div style={s.hint}>
        <span>⏰</span> User-approved orders are submitted for Alpaca processing at the next market open (9:30 AM ET). Rejected drafts are discarded.
      </div>
    </div>
  );
}

function displaySignal(signal) {
  const labels = {
    'STRONG BUY': 'Strong Buy',
    'BUY': 'Buy',
    'SELL': 'Sell',
    'STRONG SELL': 'Strong Sell',
    'NEUTRAL': 'Neutral',
    'OVERSOLD': 'Oversold',
    'OVERBOUGHT': 'Overbought',
  };
  return labels[signal] || signal || 'Signal';
}

function ProposalHeader() {
  return (
    <div style={{ ...s.row, background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '5px 18px' }}>
      <span style={{ ...s.rCheck, fontSize: 9, color: 'var(--t3)' }}></span>
      <span style={{ ...s.rSide, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}>SIDE</span>
      <span style={{ ...s.rSym, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}>SYMBOL</span>
      <span style={{ ...s.rOwned, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}>HELD</span>
      <span style={{ ...s.rQty, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}>QTY</span>
      <span style={{ ...s.rPrice, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}>PRICE</span>
      <span style={{ ...s.rCost, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}>EST COST</span>
      <span style={{ ...s.rSignal, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}>SIGNAL</span>
      <span style={{ ...s.rComp, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}>CONVICTION</span>
      <span style={{ ...s.rActions, fontSize: 9, color: 'var(--t3)', fontWeight: 600 }}></span>
    </div>
  );
}

function ProposalRow({ p, e, holdings, indicators, expanded, toggleExpand, setEdit, flagged }) {
  const qty = e.qty !== undefined ? e.qty : p.qty;
  const d = e.decision;
  const ind = indicators[p.symbol] || {};
  const isExpanded = expanded[p.proposal_id];
  const cost = qty * (p.estimated_price || 0);

  return (
    <React.Fragment>
      <div style={{
        ...s.row,
        background: d === 'approve' ? 'rgba(16,185,129,0.03)' : d === 'reject' ? 'rgba(239,68,68,0.03)' : 'transparent',
        opacity: d === 'reject' ? 0.45 : flagged ? 0.6 : 1,
        cursor: 'pointer',
      }} onClick={() => toggleExpand(p.proposal_id)}>
        <span style={s.rCheck}>
          {d === 'approve' && <span style={{ color: 'var(--green)' }}>✓</span>}
          {d === 'reject' && <span style={{ color: 'var(--red)' }}>✗</span>}
        </span>
        <span style={{ ...s.rSide, color: p.side === 'BUY' ? 'var(--green)' : 'var(--red)' }}>{p.side}</span>
        <span style={s.rSym}>
          {p.symbol}
          <span style={{ fontSize: 9, color: 'var(--t3)', marginLeft: 3 }}>{isExpanded ? '▾' : '▸'}</span>
        </span>
        <span style={s.rOwned}>{holdings[p.symbol] || 0}</span>
        <span style={s.rQty} onClick={ev => ev.stopPropagation()}>
          <input type="number" min="0" value={qty}
            onChange={ev => setEdit(p.proposal_id, { qty: parseInt(ev.target.value) || 0 })}
            style={s.qtyInput} aria-label={`Qty ${p.symbol}`} />
        </span>
        <span style={s.rPrice}>${(p.estimated_price || 0).toFixed(2)}</span>
        <span style={s.rCost}>${cost > 0 ? cost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—'}</span>
        <span style={s.rSignal}>
          <span style={{
            ...s.sigPill,
            ...(signal => {
              const sig = (signal || '').toUpperCase();
              if (sig === 'OVERSOLD') return { background: 'rgba(59,130,246,0.12)', color: 'rgb(59,130,246)' };
              if (sig === 'OVERBOUGHT') return { background: 'rgba(245,158,11,0.12)', color: 'rgb(245,158,11)' };
              if (sig.includes('STRONG')) return { background: 'var(--green-bg)', color: 'var(--green)' };
              if (sig.includes('BUY')) return { background: 'rgba(16,185,129,0.08)', color: 'rgba(16,185,129,0.8)' };
              if (sig.includes('SELL')) return { background: 'rgba(239,68,68,0.08)', color: 'rgba(239,68,68,0.8)' };
              return { background: 'var(--bg-3)', color: 'var(--t2)' };
            })(p.signal),
          }}>{displaySignal(p.signal)}</span>
        </span>
        <span style={{ ...s.rComp, color: (p.composite || 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
          {p.conviction ? p.conviction.charAt(0).toUpperCase() + p.conviction.slice(1) : ((p.composite || 0) >= 0 ? '+' : '') + (p.composite || 0).toFixed(2)}
        </span>
        <span style={s.rActions} onClick={ev => ev.stopPropagation()}>
          <button onClick={() => setEdit(p.proposal_id, { decision: d === 'approve' ? undefined : 'approve' })}
            style={{ ...s.actBtn, ...(d === 'approve' ? { background: 'var(--green-bg)', color: 'var(--green)', borderColor: 'rgba(16,185,129,0.4)' } : {}) }}
            aria-label={`Approve ${p.symbol}`}>✓</button>
          <button onClick={() => setEdit(p.proposal_id, { decision: d === 'reject' ? undefined : 'reject' })}
            style={{ ...s.actBtn, ...(d === 'reject' ? { background: 'var(--red-bg)', color: 'var(--red)', borderColor: 'rgba(239,68,68,0.4)' } : {}) }}
            aria-label={`Reject ${p.symbol}`}>✗</button>
        </span>
      </div>
      {isExpanded && (
        <div style={s.indPanel}>
          {p.rationale && (
            <div style={s.rationaleBox}>
              <span style={s.rationaleLabel}>AI Rationale</span>
              <span style={s.rationaleText}>{p.rationale}</span>
            </div>
          )}
          {p.key_indicators && (
            <div style={{ fontSize: 11, color: 'var(--t2)', marginBottom: 6 }}>
              <span style={{ fontWeight: 600, color: 'var(--t3)', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5 }}>Key Indicators: </span>
              {p.key_indicators}
            </div>
          )}
          {p.trend && (
            <div style={{ fontSize: 11, color: 'var(--t2)', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: 'var(--t3)', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5 }}>Trend: </span>
              {p.trend}
            </div>
          )}
          {ind.error ? (
            <span style={{ color: 'var(--t3)', fontSize: 11 }}>Failed to load indicators</span>
          ) : !ind.date ? (
            <span style={{ color: 'var(--t3)', fontSize: 11 }}>Loading...</span>
          ) : (
            <>
              <div style={s.indDate}>
                As of {ind.date} · Close: ${ind.close != null ? Number(ind.close).toFixed(2) : '—'}
                {ind.volume != null && ` · Vol: ${(ind.volume / 1e6).toFixed(1)}M`}
              </div>
              <div style={s.indSection}>Verdicts</div>
              <div style={s.indGrid}>
                {[['osc_verdict','Oscillators'],['ma_verdict','Moving Avg'],['strat_verdict','Strategies'],['ci_verdict','Custom Ind'],['tv_verdict','TradingView']].map(([k,l]) => (
                  <div key={k} style={s.indItem}>
                    <span style={s.indLabel}>{l}</span>
                    <span style={{ ...s.indValue, color: verdictColor(ind[k]) }}>{ind[k] || '—'}</span>
                  </div>
                ))}
                <div style={s.indItem}>
                  <span style={s.indLabel}>Overall</span>
                  <span style={{ ...s.indValue, fontWeight: 700, color: verdictColor(ind.consensus) }}>{ind.consensus || '—'}</span>
                </div>
              </div>
              {ind.oscillators && (
                <>
                  <div style={s.indSection}>Oscillators ({ind.osc_buy || 0} buy · {ind.osc_sell || 0} sell)</div>
                  <div style={s.indGrid}>
                    {Object.entries(ind.oscillators).map(([k, v]) => (
                      <div key={k} style={s.indItem}>
                        <span style={s.indLabel}>{k.replace('osc_', '').toUpperCase()}</span>
                        <span style={{ ...s.indValue, color: sigColor(v) }}>{v || '—'}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {ind.moving_averages && (
                <>
                  <div style={s.indSection}>Moving Averages ({ind.ma_buy || 0} buy · {ind.ma_sell || 0} sell)</div>
                  <div style={s.indGrid}>
                    {Object.entries(ind.moving_averages).map(([k, v]) => (
                      <div key={k} style={s.indItem}>
                        <span style={s.indLabel}>{k.replace('ma_', '').toUpperCase()}</span>
                        <span style={{ ...s.indValue, color: sigColor(v) }}>{v || '—'}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {ind.custom_indicators && (
                <>
                  <div style={s.indSection}>Custom Indicators</div>
                  <div style={s.indGrid}>
                    {Object.entries(ind.custom_indicators).map(([k, v]) => (
                      <div key={k} style={s.indItem}>
                        <span style={s.indLabel}>{k.replace('ci_', '').toUpperCase()}</span>
                        <span style={{ ...s.indValue, color: sigColor(v) }}>{v || '—'}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {ind.strategies && (
                <>
                  <div style={s.indSection}>Strategies ({ind.strat_buy || 0} buy · {ind.strat_sell || 0} sell)</div>
                  <div style={s.indGrid}>
                    {Object.entries(ind.strategies).map(([k, v]) => (
                      <div key={k} style={s.indItem}>
                        <span style={s.indLabel}>{k.replace(/_/g, ' ')}</span>
                        <span style={{ ...s.indValue, color: stratColor(v) }}>{v || '—'}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {ind.crossover_events && (
                <>
                  <div style={s.indSection}>Crossover Events ({ind.ev_bull || 0} bull · {ind.ev_bear || 0} bear)</div>
                  <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.6 }}>
                    {ind.crossover_events.split(',').map((ev, i) => (
                      <span key={i} style={s.evTag}>{ev.trim()}</span>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </React.Fragment>
  );
}

const IND_KEYS = []; // unused, kept for compat

function sigColor(v) {
  if (!v) return 'var(--t3)';
  if (v === 'BUY') return 'var(--green)';
  if (v === 'SELL') return 'var(--red)';
  return 'var(--t3)';
}

function stratColor(v) {
  if (!v) return 'var(--t3)';
  const u = String(v).toUpperCase();
  if (u.includes('STRONG BULL')) return 'var(--green)';
  if (u.includes('BULL')) return 'rgba(16,185,129,0.8)';
  if (u.includes('STRONG BEAR')) return 'var(--red)';
  if (u.includes('BEAR')) return 'rgba(239,68,68,0.8)';
  if (u.includes('REVERSE BULL')) return 'rgba(16,185,129,0.6)';
  if (u.includes('REVERSE BEAR')) return 'rgba(239,68,68,0.6)';
  return 'var(--t3)';
}

function verdictColor(v) {
  if (!v) return 'var(--t3)';
  if (v === 'STRONG BUY') return 'var(--green)';
  if (v === 'BUY') return 'rgba(16,185,129,0.8)';
  if (v === 'STRONG SELL') return 'var(--red)';
  if (v === 'SELL') return 'rgba(239,68,68,0.8)';
  return 'var(--t3)';
}

function fmtInd() { return ''; }
function indColor() { return 'var(--t1)'; }

const s = {
  skel: {
    background: 'linear-gradient(90deg, var(--bg-2) 25%, var(--bg-3) 50%, var(--bg-2) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: 3, height: 14,
  },
  loading: { padding: 40, color: 'var(--t3)', fontSize: 14 },
  h1: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 13, color: 'var(--t3)' },
  header: { marginBottom: 16 },
  empty: { textAlign: 'center', padding: '60px 40px', background: 'var(--bg-1)', borderRadius: 'var(--rl)', border: '1px solid var(--border)' },
  emptyTitle: { fontSize: 16, fontWeight: 600, marginBottom: 6 },
  emptyText: { fontSize: 13, color: 'var(--t3)' },
  demoNotice: {
    marginBottom: 16, padding: '10px 14px', background: 'var(--amber-bg)',
    border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--r)',
    color: 'var(--amber)', fontSize: 12, lineHeight: 1.5,
  },
  portfolioBar: {
    display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16, padding: '12px 20px',
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
  },
  pStat: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  pLabel: { fontSize: 10, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.5 },
  pValue: { fontSize: 14, fontWeight: 700, color: 'var(--t1)' },
  pDivider: { width: 1, height: 32, background: 'var(--border)', margin: '0 16px' },

  // Global bar
  globalBar: {
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, padding: '10px 14px',
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
  },
  gApprove: { padding: '5px 14px', background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  gReject: { padding: '5px 14px', background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  gClear: { padding: '5px 14px', background: 'var(--bg-2)', color: 'var(--t3)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  gSpacer: { flex: 1 },
  gCount: { fontSize: 12, fontWeight: 600 },

  filterBar: {
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '8px 14px',
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
  },
  filterLabel: { fontSize: 11, fontWeight: 600, color: 'var(--t3)', marginRight: 4 },
  filterChip: {
    fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 14,
    border: '1.5px solid var(--border)', cursor: 'pointer', userSelect: 'none',
    transition: 'all 0.1s',
  },
  filterQuick: {
    fontSize: 10, color: 'var(--t3)', background: 'none', border: 'none',
    cursor: 'pointer', textDecoration: 'underline', marginLeft: 4,
  },

  // Group
  group: { background: 'var(--bg-1)', borderRadius: 'var(--rl)', border: '1px solid var(--border)', marginBottom: 12, overflow: 'hidden' },
  groupHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 18px', cursor: 'pointer', userSelect: 'none',
    borderBottom: '1px solid var(--border-s)',
  },
  groupLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  groupIcon: { width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 },
  groupLabel: { fontSize: 14, fontWeight: 600 },
  groupCount: { fontSize: 12, color: 'var(--t3)', background: 'var(--bg-3)', padding: '1px 8px', borderRadius: 10 },
  miniPill: { fontSize: 11, fontWeight: 700, marginLeft: 4 },
  groupActions: { display: 'flex', alignItems: 'center', gap: 4 },
  gBtn: { padding: '4px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  chevron: { color: 'var(--t3)', fontSize: 12, marginLeft: 8, width: 16, textAlign: 'center' },

  // Row
  row: {
    display: 'flex', alignItems: 'center', padding: '7px 18px', gap: 4,
    fontSize: 13, borderBottom: '1px solid var(--border-s)', transition: 'all 0.1s',
  },
  rCheck: { width: 24, textAlign: 'center', fontWeight: 700, fontSize: 14 },
  rSide: { width: 40, fontSize: 11, fontWeight: 700 },
  rSym: { width: 60, fontWeight: 600 },
  rOwned: { width: 40, fontSize: 11, color: 'var(--t3)', textAlign: 'center' },
  rQty: { width: 56 },
  qtyInput: {
    width: 46, padding: '3px 4px', background: 'var(--bg-2)', border: '1px solid var(--border)',
    borderRadius: 4, color: 'var(--t1)', fontSize: 13, textAlign: 'center',
  },
  rPrice: { width: 72, textAlign: 'right', color: 'var(--t2)', fontSize: 12 },
  rCost: { width: 72, textAlign: 'right', color: 'var(--t2)', fontSize: 12, fontWeight: 600 },
  rSignal: { width: 95 },
  sigPill: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4 },
  rComp: { width: 50, textAlign: 'right', fontWeight: 600, fontSize: 13 },
  rActions: { display: 'flex', gap: 3, marginLeft: 'auto' },
  actBtn: {
    width: 26, height: 26, borderRadius: 5,
    borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border)',
    background: 'var(--bg-2)', color: 'var(--t3)', fontSize: 13, display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontWeight: 700,
  },

  // Submit
  submitBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 20, marginTop: 16, padding: '14px 20px',
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
    position: 'sticky', bottom: 16, boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
  },
  submitLeft: { fontSize: 13, minWidth: 0 },
  costSummary: { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8, fontSize: 12, color: 'var(--t2)' },
  ackLabel: {
    display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10,
    color: 'var(--t2)', fontSize: 12, lineHeight: 1.45, maxWidth: 720,
  },
  ackCheckbox: { marginTop: 2, accentColor: 'var(--green)', width: 15, height: 15, flexShrink: 0 },
  submitBtn: {
    padding: '10px 28px', background: 'var(--green)', color: '#000',
    border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
    whiteSpace: 'nowrap', flexShrink: 0,
  },
  hint: {
    display: 'flex', alignItems: 'center', gap: 8, marginTop: 16,
    fontSize: 12, color: 'var(--t3)', padding: '10px 14px',
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border-s)',
  },
  budgetBar: {
    display: 'flex', alignItems: 'center', gap: 0, marginBottom: 10, padding: '10px 20px',
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
  },
  summaryBar: {
    display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10, padding: '8px 14px',
    background: 'var(--bg-1)', borderRadius: 'var(--r)', border: '1px solid var(--border)',
    fontSize: 12,
  },
  indPanel: {
    padding: '10px 18px 12px', background: 'var(--bg-2)', borderBottom: '1px solid var(--border-s)',
  },
  indDate: { fontSize: 10, color: 'var(--t3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  indSection: { fontSize: 10, fontWeight: 700, color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 10, marginBottom: 4, borderBottom: '1px solid var(--border-s)', paddingBottom: 3 },
  indGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '3px 10px' },
  indItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' },
  indLabel: { fontSize: 9, color: 'var(--t3)', fontWeight: 500, textTransform: 'uppercase' },
  indValue: { fontSize: 11, fontWeight: 600, color: 'var(--t1)' },
  evTag: {
    display: 'inline-block', fontSize: 10, padding: '1px 6px', margin: '2px 3px 2px 0',
    background: 'var(--bg-3)', borderRadius: 3, color: 'var(--t2)',
  },
  rationaleBox: {
    display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10, padding: '8px 12px',
    background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)',
    borderRadius: 6,
  },
  rationaleLabel: {
    fontSize: 9, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  rationaleText: { fontSize: 12, color: 'var(--t1)', lineHeight: 1.5 },
  submittedBox: {
    marginBottom: 16, padding: '16px 20px', background: 'rgba(16,185,129,0.04)',
    border: '1px solid rgba(16,185,129,0.2)', borderRadius: 'var(--rl)',
  },
  submittedHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  submittedTitle: { fontSize: 16, fontWeight: 700, color: 'var(--green)' },
  submittedRefresh: {
    marginLeft: 'auto', padding: '5px 14px', background: 'var(--bg-2)',
    border: '1px solid var(--border)', borderRadius: 6, fontSize: 12,
    color: 'var(--t2)', cursor: 'pointer', fontWeight: 600,
  },
  submittedTable: { display: 'flex', flexDirection: 'column', gap: 2 },
  submittedRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13 },
  submittedCell: { width: 80, textAlign: 'right' },
  todaysBanner: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '10px 16px',
    background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)',
    borderRadius: 'var(--r)', fontSize: 13,
  },
  todaysBannerText: { color: 'var(--t2)', fontWeight: 500 },
};
